import type { AppContext } from '@/utils/headers';
import { cookieStore } from '../cookie';
import { getRequestContext } from '../headers';

/** Client identity from the launch URL — see app-context/server.ts. */
function getLaunchParams() {
	const { searchParams } = new URL(window.location.href);
	return {
		clientId: searchParams.get('clientId') || undefined,
		apiKey: searchParams.get('apiKey') || undefined,
	};
}

// Recover the per-client theme on the client from the SSR-injected
// `<style id="app-theme">`. The theme CSS is computed server-side only
// (app-context/server.ts), so the client context can't regenerate it — but it's
// already in the DOM. The root loader returns `context._theme` and renders it as
// that same <style>; when the loader re-runs on the CLIENT (e.g. router.invalidate
// after a login-status change), `context._theme` must still resolve or React
// drops the <style> and 3rd-party PWA themes reset to the default brand theme.
function getExistingTheme(): AppContext['_theme'] {
	return {
		style: document.getElementById('app-theme')?.textContent || undefined,
	};
}

// Memoize so the context identity (and the recovered theme) is stable across
// the app's lifetime — re-reading the DOM on every loader run is unnecessary and
// the <style> text never changes client-side.
let clientContext: AppContext | undefined;

/**
 * Get router context - called from getRouter()
 * call only on client side
 */
export function getClientContext(): AppContext {
	if (clientContext) return clientContext;
	// this would be useful whenever we decide to have a spa only
	// and move to shell architecture
	// Captured once: SPA navigation can drop the launch params from the URL, but
	// the client identity they established holds for the app's lifetime.
	const launch = getLaunchParams();
	const getClientConfig = () =>
		getRequestContext({
			clientId: launch.clientId,
			apiKey: launch.apiKey,
			getCookie: (key) => cookieStore.getCookie(key) || '',
			ua: navigator.userAgent,
		});
	clientContext = {
		clientConfig: getClientConfig,
		cookie: cookieStore,
		_theme: getExistingTheme(),
	};
	return clientContext;
}
